<template>
  <main class="bill-page">
    <AppNavBar :title="t('wallet.billTitle')" />

    <van-pull-refresh v-model="refreshing" @refresh="refreshList">
      <van-list
        v-model:loading="loading"
        :finished="finished"
        :finished-text="list.length ? t('common.noMore') : ''"
        @load="loadList"
      >
        <section v-if="!list.length && !loading" class="empty-bill">
          <AppEmptyState plain />
          <span>{{ t("wallet.noBillDetail") }}</span>
        </section>
        <button
          v-for="item in list"
          :key="item.id || item.createTime"
          class="bill-card"
          type="button"
          @click="openTrade(item)"
        >
          <div>
            <div data-strong class="data-label">
              {{ item.logLevelMsg || item.remark || "--" }}
            </div>
            <span>{{ formatTime(item.createTime) }}</span>
          </div>
          <div class="bill-card__summary">
            <div
              data-strong
              :class="{ positive: Number(item.addOrSubAmount || 0) > 0 }"
            >
              {{ Number(item.addOrSubAmount || 0) > 0 ? "+" : ""
              }}{{ item.addOrSubAmount || "0" }} {{ item.symbol || "" }}
            </div>
            <app-image
              class="bill-card__arrow"
              src="@/assets/images/rightArrow_icon.png"
              alt=""
            />
          </div>
        </button>
      </van-list>
    </van-pull-refresh>
  </main>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { useProfileStore } from "@/stores/profile";
import { useWalletBillStore } from "@/stores/wallet-bill";
import { navigateWithState } from "@/utils/route-state";
import { formatCardTimestamp } from "@/view/card/utils";

const { t } = useI18n();
const router = useRouter();
const profileStore = useProfileStore();
const walletBillStore = useWalletBillStore();
const list = computed(() => walletBillStore.list);
const loading = computed(() => walletBillStore.loading);
const finished = computed(() => walletBillStore.finished);
const refreshing = ref(false);

function formatTime(timestamp) {
  return formatCardTimestamp(timestamp);
}

async function refreshList() {
  await loadList(true);
  refreshing.value = false;
}

async function loadList(reset = false) {
  await walletBillStore.fetchNext({
    address: profileStore.platformAddress || null,
    reset,
  });
}

function openTrade(item) {
  if ([31, 32, 33].includes(Number(item?.logLevel))) {
    navigateWithState(router, "/wallet/invite/reward/detail", {
      namespace: "wallet-invite-reward-detail",
      payload: {
        id: item?.rewardId || item?.id || "",
      },
    });
    return;
  }

  walletBillStore.setCurrentTrade(item);
  navigateWithState(router, "/wallet/bill/detail", {
    namespace: "wallet-bill-detail",
    payload: {
      id: item?.id ?? "",
      rewardId: item?.rewardId ?? "",
      logLevel: item?.logLevel ?? "",
      symbol: item?.symbol || "",
      coin: item?.coin || item?.symbol || "",
      logLevelMsg: item?.logLevelMsg || item?.remark || "",
      addOrSubAmount: item?.addOrSubAmount || "0",
      createTime: item?.createTime || "",
      time: item?.time || "",
      amount: item?.amount || item?.addOrSubAmount || "0",
      status: item?.status ?? "",
      type: item?.type || "",
      fromAddress: item?.fromAddress || "",
      toAddress: item?.toAddress || "",
      hash: item?.hash || "",
      chainName: item?.chainName || "",
      chainImg: item?.chainImg || "",
      explorerUrl: item?.explorerUrl || "",
      usdtPrice: item?.usdtPrice || item?.price || "0",
      img: item?.img || item?.logo || "",
    },
  });
}

onMounted(() => {
  refreshList();
});
</script>

<style scoped lang="scss">
.bill-page {
  min-height: var(--app-viewport-height);
  padding: 0 15px 24px;
  background: #f5f5f5;
}

.bill-card {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  margin-top: 8px;
  padding: 12px;
  border: 0;
  border-radius: 12px;
  background: #ffffff;
  color: var(--wp-color-text);
  text-align: left;

  [data-strong],
  span {
    display: block;
  }

  .data-label {
    font-weight: 500 !important;
  }

  [data-strong] {
    font-size: 16px;
    font-weight: 700;
  }

  span {
    margin-top: 4px;
    color: var(--wp-color-sub-text);
    font-size: 13px;
  }

  > div:last-child {
    display: flex;
    align-items: center;

    [data-strong] {
      color: #ef0027;
    }

    [data-strong].positive {
      color: #07b077;
    }
  }
}

.bill-card__summary {
  .app-image {
    width: 9px;
    height: 14px;
    margin-left: 5px;
    object-fit: contain;
  }
}

.empty-bill {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 120px;
  color: var(--wp-color-sub-text);
  span {
    font-size: 14px;
  }
  .app-empty-state__image {
    width: 120px;
    height: 120px;
  }
}
</style>
